// Diagnostic profile comparison, not a calibrated prediction of match results.
export function summarizeLineupBalance(players, assignments) {
    const numeric = (value) => {
        if (value === null || value === undefined || typeof value === 'boolean'
            || (typeof value === 'string' && value.trim() === '')) return null;
        const result = Number(value);
        return Number.isFinite(result) ? result : null;
    };
    const weights = players[0]?.dimension_weights || {};
    const selected = (group) => players.filter((player) => assignments[player.event_member_id] === group);
    const average = (group, key, goalkeeper = false) => {
        const eligible = selected(group).filter((player) => !goalkeeper || player.can_play_goalkeeper);
        const valueOf = (player) => numeric(goalkeeper ? player.goalkeeper_score : player.dimensions[key]);
        const known = eligible.filter((player) => valueOf(player) !== null);
        if (!known.length) return { value: null, estimated: false };
        const values = known.map(valueOf);
        return {
            value: goalkeeper ? Math.max(...values) : values.reduce((a, b) => a + b, 0) / values.length,
            estimated: known.some((player) => goalkeeper ? player.goalkeeper_estimated : player.estimated_dimensions?.includes(key)),
        };
    };
    const teams = {};
    for (const group of [1, 2]) {
        let sum = 0;
        let weightSum = 0;
        let estimated = false;
        for (const [key, weight] of Object.entries(weights)) {
            const activeWeight = numeric(weight);
            if (!(activeWeight > 0)) continue;
            const metric = average(group, key);
            if (metric.value === null) continue;
            sum += metric.value * activeWeight;
            weightSum += activeWeight;
            estimated ||= metric.estimated;
        }
        teams[group] = {
            count: selected(group).length,
            profile: { value: weightSum ? sum / weightSum : null, estimated },
            defence: average(group, 'role_defence'), attack: average(group, 'role_attack'),
            form: average(group, 'recent_form'), goalkeeper: average(group, null, true),
        };
    }
    const differences = {};
    for (const key of ['profile', 'defence', 'attack', 'form', 'goalkeeper']) {
        const a = teams[1][key].value;
        const b = teams[2][key].value;
        const gap = a === null || b === null ? null : Math.abs(a - b);
        differences[key] = { gap, level: gap === null ? 0 : (gap >= 2 ? 2 : (gap >= 1 ? 1 : 0)) };
    }
    // A share of total weighted profile strength, including different team sizes.
    // It must never be presented as a statistical win probability.
    const strength1 = teams[1].profile.value === null ? null : teams[1].profile.value * teams[1].count;
    const strength2 = teams[2].profile.value === null ? null : teams[2].profile.value * teams[2].count;
    const total = (strength1 ?? 0) + (strength2 ?? 0);
    const available = teams[1].count > 0 && teams[2].count > 0 && strength1 !== null && strength2 !== null && total > 0;
    const share1 = available ? Math.round(strength1 / total * 100) : null;
    const shares = { 1: share1, 2: share1 === null ? null : 100 - share1 };
    const balance = share1 === null ? null : 100 - Math.abs(shares[1] - shares[2]);
    const level = balance === null ? 0 : (balance < 80 ? 2 : (balance < 90 ? 1 : 0));
    return { teams, differences, shares, balance, level, unassigned: selected(0).length };
}
